import React from 'react';
import { Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// Pages
import Home from '@/pages/Home';
import Legal from '@/pages/Legal';
import GradeSelect from '@/pages/GradeSelect';
import ExamSetup from '@/pages/ExamSetup';
import StudentExam from '@/pages/StudentExam';
import TeacherKey from '@/pages/TeacherKey';
import Results from '@/pages/Results';
import ResultDetail from '@/pages/ResultDetail';
import Login from '@/pages/Login';
import SetupPassword from '@/pages/SetupPassword';
import AuthCallback from '@/pages/AuthCallback';
import TeacherDashboard from '@/pages/TeacherDashboard';
import StudentProgressMonitor from '@/pages/StudentProgressMonitor';
import SchoolAdminDashboard from '@/pages/SchoolAdminDashboard';
import DistrictAdminDashboard from '@/pages/DistrictAdminDashboard';
import ParentGuide from '@/pages/ParentGuide';
import ScreenerSetup from '@/pages/ScreenerSetup';
import Resources from '@/pages/Resources';
import PrintExam from '@/pages/PrintExam';
import SampleDemoClassData from '@/pages/SampleDemoClassData';
import Guide from '@/pages/Guide';
import Help from '@/pages/Help';
import StudentFeedback from '@/pages/StudentFeedback';
import ProficiencyWatch from '@/pages/ProficiencyWatch';
import StudentProgress from '@/pages/StudentProgress';
import ParentDashboard from '@/pages/ParentDashboard';
import FeatureShowcase from '@/pages/FeatureShowcase';
import MyAssessment from '@/pages/MyAssessment';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import SuperAdminGuide from '@/pages/SuperAdminGuide';
import MosaicParentView from '@/pages/MosaicParentView';
import ExamPreview from '@/pages/ExamPreview';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import SubprocessorList from '@/pages/SubprocessorList';
import RequestDemo from '@/pages/RequestDemo';
import DemoExam from '@/pages/DemoExam';
import JoinBeta from '@/pages/JoinBeta';
import MSATExam from '@/pages/MSATExam';
import GrowthTrajectoryReport from '@/pages/GrowthTrajectoryReport';
import ParentReport from '@/pages/ParentReport';
import QuoteGenerator from '@/pages/QuoteGenerator';
import StudentDetail from '@/pages/StudentDetail';
import PriorScoresImport from '@/pages/admin/PriorScoresImport';
import Pencil14Dashboard from '@/pages/Pencil14Dashboard';
import ERMAReviewerDashboard from '@/pages/ERMAReviewerDashboard';
import DemoLauncher from '@/pages/DemoLauncher';
import RepPlaybook from '@/pages/RepPlaybook';
import RepSetup from '@/pages/RepSetup';
import RepDashboard from '@/pages/RepDashboard';
import DemoInviteRedeem from '@/pages/DemoInviteRedeem';
import TePreviewOpen from '@/pages/TePreviewOpen';
import StabilityKits from '@/pages/StabilityKits';
import ProgramSwitcherPage from '@/pages/ProgramSwitcherPage';
import DemoLanding from '@/pages/DemoLanding';
import { TeacherPortalLogin, TeacherPortalHome, TeacherPortalResetPassword } from '@/pages/TeacherPortal';
import UsbEdition from '@/pages/UsbEdition';
import SealedUsbCompatibility from '@/pages/SealedUsbCompatibility';
import SealedUsbHub from '@/pages/SealedUsbHub';
import UsbFulfillmentHub from '@/pages/UsbFulfillmentHub';
import UsbManifest from '@/pages/UsbManifest';
import Support from '@/pages/Support';
// v6.41.25 — LERNO CO print-on-demand pages
import AdminLernoOrders from '@/pages/AdminLernoOrders';
import CorpusHealthDashboard from '@/pages/CorpusHealthDashboard';
import LernoDownload from '@/pages/LernoDownload';
import LernoDownloadLookup from '@/pages/LernoDownloadLookup';
import ProficiencyFidelity from '@/pages/ProficiencyFidelity';
import ContentIntegrityCatalogue from '@/pages/ContentIntegrityCatalogue';
import EnteringLevelStudio from '@/pages/EnteringLevelStudio';

// Block students from accessing a route (allow everyone else including unauthenticated)
const StudentBlockRoute = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  if (isAuthenticated && user?.role === 'student') {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Rep-shareable USB demo redirect.
// /demo/usb/<slug>           → /usb-preview/scoring/?demo=1&rep=<slug>
// /demo/usb                  → /usb-preview/scoring/?demo=1
// Optional ?scenario=<name>  → forwarded so a link can deep-link a scenario.
const UsbDemoRedirect = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  const inQ = new URLSearchParams(search);
  const scenario = inQ.get('scenario');   // day1 | midterm | endterm | null
  const q = new URLSearchParams({ demo: '1' });
  if (slug) q.set('rep', slug);
  if (scenario) q.set('scenario', scenario);
  const target = `/usb-preview/scoring/?${q.toString()}`;
  // Hard redirect — the target is a static HTML app outside React Router.
  window.location.replace(target);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    if (requiredPermission && user?.permissions?.[requiredPermission]) {
      return children;
    }
    return <Navigate to="/" replace />;
  }
  
  if (requiredPermission && user?.permissions && user.permissions[requiredPermission] === false) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/setup-password" element={<SetupPassword />} />
      <Route path="/teacher-portal/login" element={<TeacherPortalLogin />} />
      <Route path="/teacher-portal/reset-password" element={<TeacherPortalResetPassword />} />
      <Route path="/teacher-portal" element={<TeacherPortalHome />} />
      <Route path="/support" element={<Support />} />
      {/* Sealed MOSAIC USB Edition — teacher-only shell, no marketing chrome */}
      <Route path="/usb-edition/*" element={<UsbEdition />} />
      {/* v6.43.3 — Compatibility matrix (live probe + hand-tested device grid).
          Same self-contained HTML ships on every drive; this Super Admin
          route iframes it so IT admins can review before ordering. */}
      <Route path="/admin/sealed-usb-compatibility" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <SealedUsbCompatibility />
        </ProtectedRoute>
      } />
      {/* v6.43.4 — Sealed USB Hub: one-door super-admin page for QC
          images, flash guides, retail insert, and mint history. */}
      <Route path="/admin/sealed-usb" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <SealedUsbHub />
        </ProtectedRoute>
      } />

      {/* UX1A · USB Fulfillment Hub — business-language layer */}
      <Route path="/admin/usbs" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <UsbFulfillmentHub />
        </ProtectedRoute>
      } />
      {/* v6.46 — Printable shipping / audit manifest for one order */}
      <Route path="/admin/usbs/:order_id/manifest" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <UsbManifest />
        </ProtectedRoute>
      } />
      {/*
        Rep-shareable USB demo — pretty URL for reps to text/email districts.
        Redirects to the static scoring app with the demo + rep query params.
      */}
      <Route path="/demo/usb"       element={<UsbDemoRedirect />} />
      <Route path="/demo/usb/:slug" element={<UsbDemoRedirect />} />
      <Route path="/demo-invite/:token" element={<DemoInviteRedeem />} />
      <Route path="/te-preview/:token" element={<TePreviewOpen />} />
      <Route path="/rep-setup/:token" element={<RepSetup />} />
      <Route path="/rep-dashboard" element={
        <ProtectedRoute allowedRoles={['sales_rep', 'super_admin']}>
          <RepDashboard />
        </ProtectedRoute>
      } />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/grades" element={<GradeSelect />} />
      <Route path="/my-assessment" element={
        <ProtectedRoute allowedRoles={['student']}>
          <MyAssessment />
        </ProtectedRoute>
      } />
      <Route path="/exam/setup/:gradeLevel" element={<ExamSetup />} />
      <Route 
        path="/exam/preview/:gradeLevel" 
        element={
          <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
            <ExamPreview />
          </ProtectedRoute>
        } 
      />
      <Route path="/exam/:sessionId" element={<StudentExam />} />
      <Route path="/msat/:gradeLevel" element={<MSATExam />} />
      <Route path="/growth-trajectory/:studentId" element={<ProtectedRoute><GrowthTrajectoryReport /></ProtectedRoute>} />
      <Route path="/parent-report/:studentId" element={<ProtectedRoute><ParentReport /></ProtectedRoute>} />
      <Route path="/screener" element={<ScreenerSetup />} />
      <Route path="/resources" element={
        <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin']}>
          <Resources />
        </ProtectedRoute>
      } />
      <Route path="/print-exam/:gradeLevel" element={
        <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
          <PrintExam />
        </ProtectedRoute>
      } />
      <Route path="/guide" element={<Guide />} />
      <Route path="/audit" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <ContentIntegrityCatalogue />
        </ProtectedRoute>
      } />
      <Route path="/entering-level" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <EnteringLevelStudio />
        </ProtectedRoute>
      } />
      <Route path="/help" element={<Help />} />
      {/* v6.32.168 — Public parent/guardian magic-link growth view (no auth) */}
      <Route path="/m/parent/:token" element={<MosaicParentView />} />
      <Route path="/features" element={<StudentBlockRoute><FeatureShowcase /></StudentBlockRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/subprocessors" element={<SubprocessorList />} />
      {/* v6.32.180 — Public WIDA fidelity transparency page (no auth) */}
      <Route path="/about/proficiency-fidelity" element={<ProficiencyFidelity />} />
      <Route path="/request-demo" element={<RequestDemo />} />
      <Route path="/try-demo" element={<DemoExam />} />
      {/* v6.32.146 Workstream C: /demo is now the public landing page.
          The actual demo exam still lives at /try-demo for backwards
          compatibility with existing deep links. */}
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/accessibility" element={<Legal />} />
      <Route path="/join-beta" element={<JoinBeta />} />
      <Route path="/quote" element={<QuoteGenerator />} />
      {/* v6.38.11 — canonical marketing URL for the pencil14 QR campaign.
          Renders the same QuoteGenerator so utm_* search params flow through
          to the beacon inside QuoteGenerator (a <Navigate> would drop them). */}
      <Route path="/get-a-quote" element={<QuoteGenerator />} />
      <Route path="/admin/pencil14" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <Pencil14Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/stability-kits" element={<StabilityKits />} />
      <Route path="/program/switch" element={
        <ProtectedRoute>
          <ProgramSwitcherPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher-key" element={
        <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
          <TeacherKey />
        </ProtectedRoute>
      } />
      <Route path="/teacher-key/:gradeLevel" element={
        <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
          <TeacherKey />
        </ProtectedRoute>
      } />
      <Route path="/results" element={
        <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
          <Results />
        </ProtectedRoute>
      } />
      <Route path="/results/:sessionId" element={<ResultDetail />} />
      <Route path="/my-feedback" element={<StudentFeedback />} />
      <Route path="/my-progress" element={<StudentProgress />} />
      <Route
        path="/proficiency-watch"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'district_admin']}>
            <ProficiencyWatch />
          </ProtectedRoute>
        }
      />
      <Route path="/parent" element={<ParentDashboard />} />
      <Route 
        path="/parent-guide" 
        element={
          <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
            <ParentGuide />
          </ProtectedRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/teacher" 
        element={
          <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
            <TeacherDashboard />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/teacher/student-progress"
        element={
          <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
            <StudentProgressMonitor />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/school" 
        element={
          <ProtectedRoute allowedRoles={['school_admin', 'district_admin', 'super_admin']}>
            <SchoolAdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/district" 
        element={
          <ProtectedRoute allowedRoles={['district_admin', 'super_admin']}>
            <DistrictAdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/superadmin" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/superadmin/guide" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminGuide />
          </ProtectedRoute>
        } 
      />
      {/* v6.33.9 — Sample Demo Class Data preview (deterministic roster + reports) */}
      <Route
        path="/superadmin/sample-demo-class/:band"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sales_rep']}>
            <SampleDemoClassData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/erma-review"
        element={
          <ProtectedRoute allowedRoles={['erma_reviewer', 'super_admin']}>
            <ERMAReviewerDashboard />
          </ProtectedRoute>
        }
      />
      {/* v6.32.116 — Universal sales-rep demo launcher */}
      <Route
        path="/demo-launcher"
        element={
          <ProtectedRoute allowedRoles={['sales_rep_universal']}>
            <DemoLauncher />
          </ProtectedRoute>
        }
      />

      {/* v6.37.14 — Rep Playbook (Sales Team Training & Call Playbook).
          Same content as the Super Admin Presentation Playbook tab, but
          reachable directly by sales reps from /demo-launcher. */}
      <Route
        path="/rep-playbook"
        element={
          <ProtectedRoute
            allowedRoles={['sales_rep_universal', 'super_admin', 'district_admin']}
          >
            <RepPlaybook />
          </ProtectedRoute>
        }
      />

      {/* v6.41.25 — LERNO CO print-on-demand pipeline.
          /admin/lerno-orders: super-admin only order manager.
          /download/:token: public no-login school-facing download page. */}
      <Route
        path="/admin/lerno-orders"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminLernoOrders />
          </ProtectedRoute>
        }
      />
      {/* v6.42.0 — Corpus Depth Dashboard: super-admin-only view of
          question counts by grade × standard × form, audio & PDF
          coverage percentages, and trend over time. */}
      <Route
        path="/admin/corpus-health"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CorpusHealthDashboard />
          </ProtectedRoute>
        }
      />
      {/* Phase D1/D2 — Prior-year scores admin import + student detail */}
      <Route
        path="/admin/prior-scores/import"
        element={
          <ProtectedRoute allowedRoles={['district_admin', 'super_admin']}>
            <PriorScoresImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:studentId"
        element={
          <ProtectedRoute allowedRoles={['teacher', 'school_admin', 'district_admin', 'super_admin']}>
            <StudentDetail />
          </ProtectedRoute>
        }
      />
      <Route path="/download/:token" element={<LernoDownload />} />
      <Route path="/download-lookup" element={<LernoDownloadLookup />} />

      {/* v6.32.111 — Legacy v3.16.4 deep-link redirects.
          These are defensive 1-line redirects that catch any external
          bookmark / SSO / LMS / partner email still pointing at the
          old MOSAIC URL conventions. Each redirect is purely additive:
          if no one ever hits the URL, the route is inert. If someone
          does, they land on the v6.32.108 equivalent instead of a 404.
          Documented in /app/memory/MIGRATION_REDIRECT_MAP.md. */}
      <Route path="/admin" element={<LegacyDashboardRedirect />} />
      <Route path="/admin/dashboard" element={<LegacyDashboardRedirect />} />
      <Route path="/dashboard" element={<LegacyDashboardRedirect />} />
      <Route path="/teacher-dashboard" element={<Navigate to="/teacher" replace />} />
      <Route path="/super-admin" element={<Navigate to="/superadmin" replace />} />
      <Route path="/sign-in" element={<Navigate to="/login" replace />} />
      <Route path="/signin" element={<Navigate to="/login" replace />} />
      <Route path="/pricing" element={<Navigate to="/quote" replace />} />
      <Route path="/exams" element={<Navigate to="/grades" replace />} />
      <Route path="/exam-results/:sessionId" element={<LegacyExamResultsRedirect />} />
      <Route path="/parents/:studentId/report" element={<LegacyParentReportRedirect />} />
    </Routes>
  );
};

// v6.32.111 — Role-aware redirect for /admin, /admin/dashboard, /dashboard.
// In v3.16.4 these were a single shared dashboard surface; v6.32.99/102
// split them into role-specific routes (teacher, school_admin, district_admin,
// super_admin, student, parent, erma_reviewer). This component reads the
// authenticated user's role and routes them to the correct role-specific
// dashboard. Anonymous users land on /login.
const LegacyDashboardRedirect = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const targetByRole = {
    super_admin: '/superadmin',
    district_admin: '/admin/district',
    school_admin: '/admin/school',
    teacher: '/teacher',
    student: '/my-assessment',
    parent: '/parent',
    erma_reviewer: '/erma-review',
    sales_rep_universal: '/demo-launcher',
  };
  return <Navigate to={targetByRole[user?.role] || '/'} replace />;
};

// v6.32.111 — /exam-results/:sessionId  →  /results/:sessionId
const LegacyExamResultsRedirect = () => {
  const { sessionId } = useParams();
  return <Navigate to={`/results/${sessionId}`} replace />;
};

// v6.32.111 — /parents/:studentId/report  →  /parent-report/:studentId
const LegacyParentReportRedirect = () => {
  const { studentId } = useParams();
  return <Navigate to={`/parent-report/${studentId}`} replace />;
};

export default AppRouter;
